import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { api } from '@/lib/api-client';
import { useAuthStore } from '@/stores/useAuthStore';
import { BottomNav } from '@/components/nav/BottomNav';
import { BackLink } from '@/components/ui/PageState';
import { Icon, IconTile } from '@/components/ui/icons';
import { isGerman } from '@/i18n/locale';

interface SessionRow {
  id: string;
  title: string | null;
  turn_count: number;
  last_active_at: string;
  created_at: string;
  baby_id: string | null;
}

export default function ChatHistory() {
  const user = useAuthStore((s) => s.user);
  const [sessions, setSessions] = useState<SessionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const de = isGerman();

  useEffect(() => {
    api.sessions
      .list()
      .then((res) => {
        const sorted = [...res.sessions].sort((a, b) =>
          a.last_active_at < b.last_active_at ? 1 : -1,
        );
        setSessions(sorted as SessionRow[]);
      })
      .catch((err) =>
        setError(
          (err as Error).message ?? (de ? 'Chats konnten nicht geladen werden.' : 'Failed to load chats.'),
        ),
      );
  }, [de]);

  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="flex flex-col h-[100dvh] bg-cream">
      <header className="shrink-0 border-b border-primary-50 bg-cream/95 backdrop-blur sticky top-0 z-10">
        <div className="mx-auto max-w-chat px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-1">
            <BackLink to="/chat" label={de ? 'Zurück zum Chat' : 'Back to chat'} />
            <h1 className="font-display text-lg font-semibold text-slate">
              {de ? 'Frühere Chats' : 'Past chats'}
            </h1>
          </div>
          <Link
            to="/chat"
            className="inline-flex items-center gap-1 text-sm text-primary-700 hover:text-primary-800 hover:bg-primary-50 font-medium px-2.5 py-2 rounded-lg transition-colors"
          >
            <Icon.Plus size={16} />
            {de ? 'Neuer Chat' : 'New chat'}
          </Link>
        </div>
      </header>

      <main className="flex-1 min-h-0 overflow-y-auto">
        <div className="mx-auto max-w-chat px-4 py-5">
          {error && (
            <div className="rounded-xl border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger-700 mb-4">
              {error}
            </div>
          )}

          {sessions === null && !error && (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <div key={i} className="card p-4 animate-pulse">
                  <div className="h-4 w-2/3 bg-primary-100 rounded" />
                  <div className="h-3 w-1/3 bg-primary-50 rounded mt-2" />
                </div>
              ))}
            </div>
          )}

          {sessions && sessions.length === 0 && <EmptyHistory />}

          {sessions && sessions.length > 0 && (
            <ul className="space-y-2">
              {sessions.map((s) => (
                <SessionRowView key={s.id} session={s} />
              ))}
            </ul>
          )}
        </div>
      </main>

      <BottomNav />
    </div>
  );
}

function SessionRowView({ session }: { session: SessionRow }) {
  const de = isGerman();
  const title = session.title?.trim() || (de ? 'Unbenanntes Gespräch' : 'Untitled conversation');
  const turnsLabel = session.turn_count === 1
    ? (de ? '1 Nachricht' : '1 message')
    : (de ? `${session.turn_count} Nachrichten` : `${session.turn_count} messages`);
  const when = formatWhen(session.last_active_at, de);

  return (
    <li>
      <Link
        to={`/chat/${session.id}`}
        className="card p-4 flex items-center gap-3 hover:shadow-md hover:border-primary-200 transition-all"
      >
        <IconTile tone="primary" size="sm">
          <Icon.MessageCircle size={18} />
        </IconTile>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-slate text-[15px] truncate">{title}</div>
          <div className="text-xs text-slate-muted mt-0.5">
            {when} · {turnsLabel}
          </div>
        </div>
        <Icon.ArrowRight size={16} className="text-slate-muted shrink-0" />
      </Link>
    </li>
  );
}

function EmptyHistory() {
  const de = isGerman();
  return (
    <div className="card p-8 text-center">
      <IconTile tone="primary" className="mx-auto">
        <Icon.MessageCircle size={20} />
      </IconTile>
      <h2 className="font-display font-semibold text-slate mt-3">
        {de ? 'Noch keine Chats' : 'No chats yet'}
      </h2>
      <p className="text-sm text-slate-muted mt-2 max-w-xs mx-auto">
        {de
          ? 'Sobald du Alma etwas fragst, erscheinen deine Gespräche hier — damit du jederzeit weitermachen kannst.'
          : 'Once you ask Alma something, your conversations will live here so you can pick up where you left off.'}
      </p>
      <Link to="/chat" className="btn-primary text-sm mt-4 py-2 px-4">
        {de ? 'Gespräch starten' : 'Start a conversation'}
      </Link>
    </div>
  );
}

function formatWhen(iso: string, de: boolean): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    const time = d.toLocaleTimeString(de ? 'de-DE' : undefined, { hour: 'numeric', minute: '2-digit' });
    return de ? `heute, ${time}` : `today, ${time}`;
  }
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return de ? 'gestern' : 'yesterday';

  const days = Math.floor((now.getTime() - d.getTime()) / 86400000);
  if (days < 7) return de ? `vor ${days} Tagen` : `${days} days ago`;

  return d.toLocaleDateString(de ? 'de-DE' : undefined, { month: 'short', day: 'numeric' });
}
